import React, { useState, useEffect } from 'react';
import { megaPrizeAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Trophy, Ticket } from 'lucide-react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';

const ClientMegaPrize = ({ kakoId }) => {
  const [config, setConfig] = useState(null);
  const [myTickets, setMyTickets] = useState([]);
  const [winner, setWinner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [showRules, setShowRules] = useState(false); // NOVO

  useEffect(() => {
    if (kakoId) {
      loadData();
    }
  }, [kakoId]);

  const loadData = async () => {
    try {
      const [configRes, ticketsRes, winnerRes] = await Promise.all([
        megaPrizeAPI.getConfig(),
        megaPrizeAPI.getMyTickets(kakoId),
        megaPrizeAPI.getCurrentWinner()
      ]);
      
      setConfig(configRes.data);
      setMyTickets(ticketsRes.data);
      setWinner(winnerRes.data);
    } catch (error) {
      console.error('Erro ao carregar MEGA PRÊMIO:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  if (!config) {
    return null;
  }

  // Se evento foi finalizado (tem event_end_date), mostra resultado
  if (config.event_end_date && winner) {
      return (
        <div className="space-y-4">
          {/* Ganhador */}
          <Card className="border-4 border-green-400 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardHeader>
              <CardTitle className="text-center">🏆 EVENTO FINALIZADO</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              {winner.winner_kako_id ? (
                <>
                  {winner.winner_avatar && (
                    <div className="relative w-80 h-80 mx-auto mb-4 flex items-center justify-center">
                      {/* Avatar 32×32 centralizado, z-0 (atrás) */}
                      <img 
                        src={winner.winner_avatar} 
                        alt="" 
                        className="w-32 h-32 rounded-full absolute z-0 object-cover"
                        style={{ marginTop: '-15px' }}
                      />
                      {/* Moldura 80×80, z-10 (na frente) */}
                      <img 
                        src="https://customer-assets.emergentagent.com/job_pixcoinshop/artifacts/ggwpuckl_image.png"
                        alt="moldura"
                        className="w-full h-full object-contain absolute inset-0 z-10 pointer-events-none"
                      />
                    </div>
                  )}
                  <div style={{ marginTop: '-60px' }}>
                    <p className="text-3xl font-bold text-green-700">{winner.winner_nickname}</p>
                    <p className="text-sm text-gray-600">KAKO ID: {winner.winner_kako_id}</p>
                  </div>
                  <div className="bg-yellow-50 p-6 rounded-lg border-2 border-yellow-400">
                    <p className="text-sm text-gray-600 mb-2">Prêmio</p>
                    <p className="text-5xl font-black text-yellow-600">{winner.prize_amount?.toLocaleString() || 0}</p>
                    <p className="text-2xl text-yellow-700 mt-1">🎁 moedas</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <p className="text-xs text-gray-600 mb-2">Números Sorteados</p>
                    <div className="flex justify-center gap-2">
                      {winner.drawn_numbers_str?.split('-').map((num, idx) => (
                        <span key={idx} className="w-10 h-10 rounded-full bg-green-500 text-white font-bold flex items-center justify-center text-lg">
                          {num}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-green-600 mt-2 font-bold">
                      {winner.winner_type === '6_match' && '🎯 6 ACERTOS!'}
                      {winner.winner_type === '5_match' && '🎯 5 ACERTOS!'}
                      {winner.winner_type === '4_match' && '🎯 4 ACERTOS!'}
                    </p>
                  </div>
                </>
              ) : (
                <div>
                  <Trophy className="h-24 w-24 mx-auto text-yellow-500 mb-4" />
                  <p className="text-2xl font-bold text-gray-700">SEM GANHADOR</p>
                  <p className="text-sm text-gray-600 mt-2">Prêmio ACUMULADO para próxima rodada!</p>
                  <div className="bg-yellow-50 p-4 rounded-lg mt-4">
                    <p className="text-3xl font-bold text-yellow-600">{winner.prize_amount?.toLocaleString() || 0} 🎁</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          <p className="text-center text-sm text-gray-500">Aguardando admin reiniciar evento...</p>
        </div>
      );
    }
    
    // Evento inativo e sem resultado - Mostra último ganhador se houver
    if (winner) {
      return (
        <Card className="border-2 border-green-400">
          <CardHeader>
            <CardTitle className="text-center">🏆 ÚLTIMO GANHADOR</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            {winner.winner_avatar && (
              <img src={winner.winner_avatar} alt="" className="w-24 h-24 rounded-full mx-auto border-4 border-green-400" />
            )}
            <div>
              <p className="text-2xl font-bold">{winner.winner_nickname || 'SEM GANHADOR'}</p>
              <p className="text-sm text-gray-600">KAKO ID: {winner.winner_kako_id || '-'}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Prêmio</p>
              <p className="text-3xl font-bold text-yellow-600">{winner.prize_amount?.toLocaleString() || 0} 🎁</p>
            </div>
            <div className="bg-gray-100 p-3 rounded">
              <p className="text-xs text-gray-600 mb-2">Números Sorteados</p>
              <div className="flex justify-center gap-2">
                {winner.drawn_numbers_str?.split('-').map((num, idx) => (
                  <span key={idx} className="w-8 h-8 rounded-full bg-green-500 text-white text-sm font-bold flex items-center justify-center">
                    {num}
                  </span>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-500">
              {winner.winner_type === '6_match' && '6 acertos'}
              {winner.winner_type === '5_match' && '5 acertos'}
              {winner.winner_type === '4_match' && '4 acertos'}
              {winner.winner_type === 'accumulated' && 'Prêmio Acumulado'}
            </p>
          </CardContent>
        </Card>
      );
    }
    
    // Se evento inativo e sem resultado, não mostra nada
    if (!config.active && !config.event_end_date) {
      return (
        <Card>
          <CardContent className="text-center py-12 text-gray-500">
            <Trophy className="mx-auto h-12 w-12 mb-4 opacity-50" />
            <p>Evento MEGA PRÊMIO não está ativo</p>
          </CardContent>
        </Card>
      );
    }

  return (
    <div className="space-y-4">
      {/* Prêmio Acumulado - DESTAQUE */}
      <Card className="border-4 border-yellow-400 bg-gradient-to-br from-yellow-100 via-orange-100 to-yellow-100">
        <CardContent className="p-6 text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_pixcoinshop/artifacts/z8ldhg31_IMG_8069.PNG"
            alt="Prêmio Acumulado"
            className="h-40 mx-auto mb-4"
          />
          <p className="text-6xl font-black text-yellow-600 mb-2">
            {config.current_prize?.toLocaleString() || 0}
          </p>
          <p className="text-2xl text-yellow-700">🎁 moedas</p>
          <p className="text-sm text-gray-600 mt-4">{config.total_tickets || 0} tickets no sorteio</p>
        </CardContent>
      </Card>

      {/* Meus Tickets - Lista Simples com Filtro */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <CardTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5" />
              Meus Tickets ({myTickets.filter(t => {
                if (periodFilter === 'all') return true;
                const ticketDate = new Date(t.created_at);
                const now = new Date();
                const today = new Date(now.setHours(0,0,0,0));
                const yesterday = new Date(today.getTime() - 24*60*60*1000);
                const weekAgo = new Date(today.getTime() - 7*24*60*60*1000);
                const monthAgo = new Date(today.getTime() - 30*24*60*60*1000);
                
                if (periodFilter === 'today') return ticketDate >= today;
                if (periodFilter === 'yesterday') return ticketDate >= yesterday && ticketDate < today;
                if (periodFilter === 'week') return ticketDate >= weekAgo;
                if (periodFilter === 'month') return ticketDate >= monthAgo;
                return true;
              }).length})
            </CardTitle>
            <div className="flex gap-2">
              <select 
                className="border rounded px-2 py-1 text-xs"
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
              >
                <option value="all">Todos</option>
                <option value="today">Hoje</option>
                <option value="yesterday">Ontem</option>
                <option value="week">Semana</option>
                <option value="month">Mês</option>
              </select>
              <Button size="sm" variant="outline" onClick={() => setShowRules(true)}>
                📋 Regras
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {myTickets.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              Você ainda não tem tickets.<br/>
              <span className="text-sm">Compre pacotes ≥ {config.min_order_coins?.toLocaleString()} moedas!</span>
            </p>
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-2">
              {myTickets.filter(t => {
                if (periodFilter === 'all') return true;
                const ticketDate = new Date(t.created_at);
                const now = new Date();
                const today = new Date(now.setHours(0,0,0,0));
                const yesterday = new Date(today.getTime() - 24*60*60*1000);
                const weekAgo = new Date(today.getTime() - 7*24*60*60*1000);
                const monthAgo = new Date(today.getTime() - 30*24*60*60*1000);
                
                if (periodFilter === 'today') return ticketDate >= today;
                if (periodFilter === 'yesterday') return ticketDate >= yesterday && ticketDate < today;
                if (periodFilter === 'week') return ticketDate >= weekAgo;
                if (periodFilter === 'month') return ticketDate >= monthAgo;
                return true;
              }).map((ticket, idx) => (
                <div key={ticket.id} className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-300 rounded hover:bg-yellow-100">
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-yellow-800">#{idx + 1}</span>
                    <div className="flex gap-1">
                      {ticket.numbers_str.split('-').map((num, numIdx) => (
                        <span key={numIdx} className="w-8 h-8 rounded-full bg-yellow-500 text-white text-xs font-bold flex items-center justify-center">
                          {num}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span className="text-xs text-gray-600">{new Date(ticket.created_at).toLocaleDateString('pt-BR')}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Dialog de Regras */}
      <Dialog open={showRules} onOpenChange={setShowRules}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📋 Regras do MEGA PRÊMIO</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {config?.rules_text ? (
              <div className="whitespace-pre-line text-sm text-gray-700">
                {config.rules_text}
              </div>
            ) : (
              <div className="text-sm text-gray-700 space-y-2">
                <p>✅ Compre pacotes de <strong>{config?.min_order_coins?.toLocaleString()} moedas</strong> ou mais</p>
                <p>✅ Receba 1 ticket com 6 dezenas aleatórias (01-60)</p>
                <p>✅ Cada pedido adiciona <strong>{config?.prize_increment} moedas</strong> ao prêmio acumulado</p>
                <p>✅ Guarde seus números e aguarde o sorteio!</p>
                <p>🏆 Ganha quem acertar <strong>6, 5 ou 4 dezenas</strong></p>
                <p>💰 Se ninguém acertar, o prêmio <strong>ACUMULA</strong> para o próximo sorteio!</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientMegaPrize;
